import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { useIsMedico } from "@/hooks/useIsMedico";
import { usePermissions } from "@/hooks/usePermissions";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { Calendar as CalendarIcon, Copy, Link, Video } from "lucide-react";
import { useState } from "react";

interface MockAppointment {
  id: number;
  id_paciente: number;
  paciente_nome?: string;
  id_tipo_agenda: number;
  tipo_agenda_descricao?: string;
  id_medico: number;
  medico_nome?: string;
  data_agendamento: string;
  hora_inicio: string;
  hora_fim: string;
  sala_consultorio?: string;
  motivo?: string;
  observacoes?: string;
  prioridade: 'BAIXA' | 'MEDIA' | 'ALTA';
  status_agendamento: 'AGENDADO' | 'CONFIRMADO' | 'CANCELADO' | 'AGUARDANDO_CONFIRMACAO' | 'CONCLUIDO';
  duracao_minutos: number;
  meet_link?: string;
  notificacao_sms: boolean;
  notificacao_whatsapp: boolean;
  notificacao_email: boolean;
}

interface TipoAtendimentoAgenda {
  id: number;
  nome: string;
  cor?: string;
  habilitado_meet?: boolean;
}

interface AgendaProps {
  appointments: MockAppointment[];
  doctors: string[];
  filterDoctor: string[];
  filterStatus: string;
  filterType: string;
  viewType: "diaria" | "semanal";
  selectedDate: Date;
  periodStart: Date;
  availableTimeSlots: string[];
  diasSemanaConfigurados: string[];
  tiposAgenda?: Array<{ id: number; descricao: string; cor?: string }>;
  tiposAtendimento?: TipoAtendimentoAgenda[];
  isGoogleMeetAutoEnabled: boolean;
  onFilterDoctorChange: (value: string[]) => void;
  onFilterStatusChange: (value: string) => void;
  onFilterTypeChange: (value: string) => void;
  onViewTypeChange: (value: "diaria" | "semanal") => void;
  onPeriodStartChange: (date: Date) => void;
  onSelectedDateChange: (date: Date) => void;
  onAppointmentClick?: (appointment: MockAppointment) => void;
  onCreateMeetLink: (id: number) => Promise<void>;
}

const getStatus = (status: string) => {
  const statusMap: Record<string, { label: string; variant: "default" | "secondary" | "destructive" | "outline" }> = {
    AGENDADO: { label: "Agendado", variant: "default" },
    CONFIRMADO: { label: "Confirmado", variant: "secondary" },
    CANCELADO: { label: "Cancelado", variant: "destructive" },
    AGUARDANDO_CONFIRMACAO: { label: "Aguardando", variant: "outline" },
    CONCLUIDO: { label: "Concluído", variant: "secondary" },
  };
  const config = statusMap[status] || statusMap.AGENDADO;
  return <Badge variant={config.variant}>{config.label}</Badge>;
};

const getPrioridade = (priority: string) => {
  const priorityMap: Record<string, { label: string; className: string }> = {
    BAIXA: { label: "Baixa", className: "bg-green-500/10 text-green-700 dark:text-green-400" },
    MEDIA: { label: "Média", className: "bg-yellow-500/10 text-yellow-700 dark:text-yellow-400" },
    ALTA: { label: "Alta", className: "bg-red-500/10 text-red-700 dark:text-red-400" },
  };
  const config = priorityMap[priority] || priorityMap.MEDIA;
  return <Badge className={config.className}>{config.label}</Badge>;
};

export const Agenda = ({
  appointments,
  doctors,
  filterDoctor,
  filterStatus,
  filterType,
  viewType,
  selectedDate,
  periodStart,
  availableTimeSlots,
  diasSemanaConfigurados,
  tiposAgenda = [],
  tiposAtendimento = [],
  isGoogleMeetAutoEnabled,
  onFilterDoctorChange,
  onFilterStatusChange,
  onFilterTypeChange,
  onViewTypeChange,
  onPeriodStartChange,
  onSelectedDateChange,
  onAppointmentClick,
  onCreateMeetLink,
}: AgendaProps) => {
  // Função para obter a cor do tipo de atendimento
  const getTipoAgendaCor = (idTipoAgenda: number): string => {
    // Tenta encontrar no tiposAtendimento primeiro (novo), depois tiposAgenda (compatibilidade)
    const tipoAtendimento = tiposAtendimento.find(t => t.id === idTipoAgenda);
    if (tipoAtendimento?.cor) return tipoAtendimento.cor;

    const tipoAgenda = tiposAgenda.find(t => t.id === idTipoAgenda);
    return tipoAgenda?.cor || tipoAgenda?.cor_padrao || '#3b82f6'; // Cor padrão se não encontrar
  };
  const { toast } = useToast();
  const [isSelectOpen, setIsSelectOpen] = useState(false);

  // Verificação de permissões e cargo
  const { isAdmin } = usePermissions();
  const { isMedico } = useIsMedico();

  const handleStartAppointment = (meetLink?: string) => {
    if (!meetLink) return;
    window.open(meetLink, "_blank", "noopener,noreferrer");
  };

  const copyMeetLink = async (meetLink?: string) => {
    if (!meetLink) return;
    try {
      await navigator.clipboard.writeText(meetLink);
      toast({ title: "Link copiado!", description: "Link da video conferência copiado para a área de transferência." });
    } catch {
      toast({ title: "Erro ao copiar", variant: "destructive", description: "Não foi possível copiar o link." });
    }
  };

  const isMeetEnabledForType = (idTipoAtendimento: number): boolean => {
    const tipoAtendimento = tiposAtendimento.find(tipo => tipo.id === idTipoAtendimento);
    return Boolean(tipoAtendimento?.habilitado_meet);
  };

  const handleDoctorToggle = (doctor: string, checked: boolean) => {
    if (checked) {
      if (!filterDoctor.includes(doctor)) {
        onFilterDoctorChange([...filterDoctor, doctor]);
      }
    } else {
      onFilterDoctorChange(filterDoctor.filter((d) => d !== doctor));
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Agenda</CardTitle>
        <div className="flex flex-wrap gap-2 mt-4 items-center">
          <Select value={viewType} onValueChange={(v) => onViewTypeChange(v as "diaria" | "semanal")}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Visualização" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="diaria">Visão Diária</SelectItem>
              <SelectItem value="semanal">Visão Semanal</SelectItem>
            </SelectContent>
          </Select>

          <Select
            value={filterDoctor.length === 0 ? "all" : filterDoctor.length === 1 ? filterDoctor[0] : "multiple"}
            onValueChange={(value) => {
              if (value === "all") {
                onFilterDoctorChange([]);
              } else if (value !== "multiple") {
                // Seleciona apenas o médico escolhido
                onFilterDoctorChange([value]);
              }
            }}
            open={isSelectOpen}
            onOpenChange={setIsSelectOpen}
          >
            <SelectTrigger className="w-[250px]">
              <SelectValue>
                {filterDoctor.length === 0
                  ? "Todos os médicos"
                  : filterDoctor.length === 1
                    ? filterDoctor[0]
                    : `${filterDoctor.length} médico(s) selecionado(s)`}
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              <div
                className="relative flex cursor-default select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none hover:bg-accent focus:bg-accent"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onFilterDoctorChange([]);
                  setIsSelectOpen(false);
                }}
              >
                <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
                  <Checkbox checked={filterDoctor.length === 0} className="pointer-events-none" />
                </span>
                <span>Todos os médicos</span>
              </div>
              {doctors.map((doctor) => (
                <div
                  key={doctor}
                  className="relative flex cursor-default select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none hover:bg-accent focus:bg-accent"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleDoctorToggle(doctor, !filterDoctor.includes(doctor));
                  }}
                >
                  <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
                    <Checkbox
                      checked={filterDoctor.includes(doctor)}
                      className="pointer-events-none"
                    />
                  </span>
                  <span>{doctor}</span>
                </div>
              ))}
            </SelectContent>
          </Select>

          <Select value={filterType} onValueChange={onFilterTypeChange}>
            <SelectTrigger className="w-[200px]">
              <SelectValue placeholder="Todos os tipos" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos os tipos</SelectItem>
              {tiposAtendimento.map((tipo) => (
                <SelectItem key={tipo.id} value={tipo.id.toString()}>
                  {tipo.nome}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={filterStatus} onValueChange={onFilterStatusChange}>
            <SelectTrigger className="w-[200px]">
              <SelectValue placeholder="Todos os status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos os status</SelectItem>
              <SelectItem value="AGENDADO">Agendado</SelectItem>
              <SelectItem value="CONFIRMADO">Confirmado</SelectItem>
              <SelectItem value="CANCELADO">Cancelado</SelectItem>
              <SelectItem value="AGUARDANDO_CONFIRMACAO">Aguardando Confirmação</SelectItem>
              <SelectItem value="CONCLUIDO">Concluído</SelectItem>
            </SelectContent>
          </Select>

          <div className="flex items-center gap-2">
            <Label>Data:</Label>
            <Input
              type="date"
              value={format(periodStart, "yyyy-MM-dd")}
              onChange={(e) => {
                const newDate = new Date(e.target.value + 'T00:00:00');
                onPeriodStartChange(newDate);
                onSelectedDateChange(newDate);
              }}
            />
          </div>

          <Button
            variant="outline"
            onClick={() => {
              const today = new Date();
              today.setHours(0, 0, 0, 0);
              onPeriodStartChange(today);
              onSelectedDateChange(today);
            }}
            className="gap-2"
          >
            <CalendarIcon className="h-4 w-4" />
            Hoje
          </Button>
        </div>
      </CardHeader>

      <CardContent>
        {viewType === "diaria" ? (
          <div className="overflow-x-auto">
            <div className="min-w-[900px]">
              <div className="grid grid-cols-[100px_repeat(auto-fit,minmax(200px,1fr))] border-b border-muted-foreground/20">
                <div className="p-2 font-semibold text-sm text-muted-foreground">Horário</div>
                {((filterDoctor.length === 0 || filterDoctor.length > 1) ? ["Todos"] : filterDoctor).map((doctor) => (
                  <div
                    key={doctor}
                    className="p-2 text-center font-semibold text-sm border-l border-muted-foreground/20"
                  >
                    {doctor}
                  </div>
                ))}
              </div>

              {(() => {
                // Coleta todos os horários únicos dos agendamentos filtrados para a data selecionada
                const dataFormatada = format(selectedDate, "yyyy-MM-dd");
                const agendamentosDoDia = appointments.filter((a) => {
                  const dataAgendamento = a.data_agendamento.split('T')[0];
                  const matchesDate = dataAgendamento === dataFormatada;
                  const matchesDoctor = filterDoctor.length === 0 || filterDoctor.includes(a.medico_nome || "");
                  const matchesStatus = filterStatus === "all" || a.status_agendamento.toUpperCase() === filterStatus.toUpperCase();
                  const matchesType = filterType === "all" ||
                    (a.id_tipo_agenda && a.id_tipo_agenda.toString() === filterType) ||
                    a.tipo_agenda_descricao?.toLowerCase().includes(filterType.toLowerCase());
                  return matchesDate && matchesDoctor && matchesStatus && matchesType;
                });

                // Extrai horários únicos dos agendamentos
                const horariosAgendamentos = [...new Set(agendamentosDoDia.map(a => a.hora_inicio))].sort();

                // Combina horários disponíveis com horários dos agendamentos e remove duplicatas
                const todosHorarios = [...new Set([...availableTimeSlots, ...horariosAgendamentos])].sort();

                return todosHorarios.map((time) => {
                  // Busca todos os agendamentos deste horário que correspondem aos filtros
                  const agendamentosDoHorario = appointments.filter(
                    (a) => {
                      const dataAgendamento = a.data_agendamento.split('T')[0]; // Garante formato YYYY-MM-DD
                      const matchesDate = dataAgendamento === dataFormatada;
                      const matchesTime = a.hora_inicio === time;
                      const matchesDoctor = filterDoctor.length === 0 || filterDoctor.includes(a.medico_nome || "");
                      const matchesStatus = filterStatus === "all" || a.status_agendamento.toUpperCase() === filterStatus.toUpperCase();
                      const matchesType = filterType === "all" ||
                        (a.id_tipo_agenda && a.id_tipo_agenda.toString() === filterType) ||
                        a.tipo_agenda_descricao?.toLowerCase().includes(filterType.toLowerCase());

                      return matchesDate && matchesTime && matchesDoctor && matchesStatus && matchesType;
                    }
                  );

                  // Se há múltiplos médicos ou todos selecionados, mostra todos os agendamentos em uma única coluna
                  const mostrarTodosAgendamentos = filterDoctor.length === 0 || filterDoctor.length > 1;
                  const medicosParaExibir = mostrarTodosAgendamentos ? ["Todos"] : (filterDoctor.length === 0 ? doctors : filterDoctor);

                  return (
                    <div
                      key={time}
                      className="grid grid-cols-[100px_repeat(auto-fit,minmax(200px,1fr))] border-b border-muted-foreground/10 min-h-[60px]"
                    >
                      <div className="p-2 text-sm text-muted-foreground border-r border-muted-foreground/10">
                        {time}
                      </div>

                      {medicosParaExibir.map((doctor) => {
                        let agendamentosParaMostrar: typeof appointments = [];

                        if (mostrarTodosAgendamentos) {
                          // Mostra todos os agendamentos do horário
                          agendamentosParaMostrar = agendamentosDoHorario;
                        } else {
                          // Mostra apenas o agendamento do médico específico
                          const apt = agendamentosDoHorario.find(a => a.medico_nome === doctor);
                          if (apt) {
                            agendamentosParaMostrar = [apt];
                          }
                        }

                        return (
                          <div
                            key={`${doctor}-${time}`}
                            className="relative p-2 border-l border-muted-foreground/10 transition-all min-h-[60px]"
                          >
                            {agendamentosParaMostrar.length > 0 ? (
                              <div className="space-y-2">
                                {agendamentosParaMostrar.map((apt) => {
                                  const corBorda = getTipoAgendaCor(apt.id_tipo_agenda);
                                  return (
                                    <div
                                      key={apt.id}
                                      className="bg-primary/10 hover:bg-primary/20 cursor-pointer rounded-md p-2 transition-all"
                                      style={{
                                        borderLeftWidth: '4px',
                                        borderLeftColor: corBorda,
                                      }}
                                      onClick={() => {
                                        if (onAppointmentClick) {
                                          onAppointmentClick(apt);
                                        } else {
                                          toast({
                                            title: `Agendamento de ${apt.paciente_nome}`,
                                            description: `Médico: ${apt.medico_nome} | ${apt.hora_inicio} | ${apt.motivo || "Sem motivo informado"}`,
                                          });
                                        }
                                      }}
                                    >
                                      <div className="text-sm">
                                        <p className="font-medium">{apt.paciente_nome}</p>
                                        {mostrarTodosAgendamentos && (
                                          <p className="text-xs text-muted-foreground">{apt.medico_nome}</p>
                                        )}
                                        <p className="text-xs text-muted-foreground">
                                          {apt.motivo || "Consulta"}
                                        </p>
                                        <div className="flex gap-1 mt-1">
                                          {getStatus(apt.status_agendamento)}
                                        </div>
                                        {!apt.meet_link &&
                                          !isGoogleMeetAutoEnabled &&
                                          apt.status_agendamento !== "CANCELADO" &&
                                          isMeetEnabledForType(apt.id_tipo_agenda) && (
                                            <Button
                                              size="sm"
                                              variant="outline"
                                              className="mt-2 h-7 text-xs gap-1"
                                              onClick={async (event) => {
                                                event.stopPropagation();
                                                await onCreateMeetLink(apt.id);
                                              }}
                                            >
                                              <Link className="h-3.5 w-3.5" />
                                              Criar link Meet
                                            </Button>
                                          )}
                                        {apt.meet_link && apt.status_agendamento !== "CANCELADO" && (isAdmin || isMedico) && (
                                          <div className="flex gap-1 mt-2">
                                            <Button
                                              size="sm"
                                              className="h-7 text-xs gap-1"
                                              onClick={(event) => {
                                                event.stopPropagation();
                                                handleStartAppointment(apt.meet_link);
                                              }}
                                            >
                                              <Video className="h-3.5 w-3.5" />
                                              Iniciar atendimento
                                            </Button>
                                            <Button
                                              size="sm"
                                              variant="outline"
                                              className="h-7 text-xs gap-1"
                                              onClick={(event) => {
                                                event.stopPropagation();
                                                copyMeetLink(apt.meet_link);
                                              }}
                                              title="Copiar link da video conferência"
                                            >
                                              <Copy className="h-3.5 w-3.5" />
                                              Copiar link
                                            </Button>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            ) : (
                              <p className="text-xs text-center text-muted-foreground italic">Livre</p>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  );
                })
              })()}
            </div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <div className="min-w-[1200px]">
              {(() => {
                // Mapeia os dias da semana do backend para números (0 = domingo, 1 = segunda, etc.)
                const diaSemanaMap: Record<string, number> = {
                  'DOMINGO': 0,
                  'SEGUNDA': 1,
                  'TERCA': 2,
                  'QUARTA': 3,
                  'QUINTA': 4,
                  'SEXTA': 5,
                  'SABADO': 6
                };

                // Converte os dias configurados para números
                const diasConfiguradosNumeros = diasSemanaConfigurados
                  .map(dia => diaSemanaMap[dia])
                  .filter(num => num !== undefined)
                  .sort((a, b) => a - b);

                // Se não há dias configurados, não mostra nada
                if (diasConfiguradosNumeros.length === 0) {
                  return (
                    <div className="p-8 text-center text-muted-foreground">
                      Nenhum dia da semana configurado para os médicos selecionados.
                    </div>
                  );
                }

                // Calcula as datas para cada dia configurado
                // Encontra a data de cada dia da semana configurado a partir do periodStart
                const hoje = periodStart.getDay(); // 0 = domingo, 1 = segunda, etc.

                const diasDaSemana = diasConfiguradosNumeros.map(diaNum => {
                  let diasParaAdicionar = diaNum - hoje;

                  // Se o dia já passou nesta semana, busca na próxima semana
                  if (diasParaAdicionar < 0) {
                    diasParaAdicionar += 7;
                  }

                  const date = new Date(periodStart);
                  date.setHours(0, 0, 0, 0);
                  date.setDate(periodStart.getDate() + diasParaAdicionar);
                  return { diaNum, date, diaNome: diasSemanaConfigurados.find(d => diaSemanaMap[d] === diaNum) || '' };
                });

                const numColunas = diasDaSemana.length;

                return (
                  <>
                    <div
                      className="grid border-b border-muted-foreground/20"
                      style={{ gridTemplateColumns: `100px repeat(${numColunas}, 1fr)` }}
                    >
                      <div className="p-2 font-semibold text-sm text-muted-foreground">Horário</div>
                      {diasDaSemana.map(({ date, diaNome }, index) => (
                        <div
                          key={index}
                          className="p-2 text-center font-semibold text-sm border-l border-muted-foreground/20"
                        >
                          {format(date, "EEE dd/MM", { locale: ptBR })}
                        </div>
                      ))}
                    </div>

                    {(() => {
                      // Coleta todos os horários únicos dos agendamentos filtrados para os dias da semana
                      const agendamentosDaSemana = appointments.filter((a) => {
                        const dataAgendamento = a.data_agendamento.split('T')[0];
                        const matchesDate = diasDaSemana.some(({ date }) => format(date, "yyyy-MM-dd") === dataAgendamento);
                        const matchesDoctor = filterDoctor.length === 0 || filterDoctor.includes(a.medico_nome || "");
                        const matchesStatus = filterStatus === "all" || a.status_agendamento.toUpperCase() === filterStatus.toUpperCase();
                        const matchesType = filterType === "all" ||
                          (a.id_tipo_agenda && a.id_tipo_agenda.toString() === filterType) ||
                          a.tipo_agenda_descricao?.toLowerCase().includes(filterType.toLowerCase());
                        return matchesDate && matchesDoctor && matchesStatus && matchesType;
                      });

                      // Extrai horários únicos dos agendamentos
                      const horariosAgendamentos = [...new Set(agendamentosDaSemana.map(a => a.hora_inicio))].sort();

                      // Combina horários disponíveis com horários dos agendamentos e remove duplicatas
                      const todosHorarios = [...new Set([...availableTimeSlots, ...horariosAgendamentos])].sort();

                      return todosHorarios.map((time) => (
                        <div
                          key={time}
                          className="grid border-b border-muted-foreground/10 min-h-[60px]"
                          style={{ gridTemplateColumns: `100px repeat(${numColunas}, 1fr)` }}
                        >
                          <div className="p-2 text-sm text-muted-foreground border-r border-muted-foreground/10">
                            {time}
                          </div>
                          {diasDaSemana.map(({ date }, index) => {
                            // Busca todos os agendamentos deste horário/data que correspondem aos filtros
                            const dataFormatada = format(date, "yyyy-MM-dd");
                            const agendamentosDoHorario = appointments.filter(
                              (a) => {
                                const dataAgendamento = a.data_agendamento.split('T')[0]; // Garante formato YYYY-MM-DD
                                const matchesDate = dataAgendamento === dataFormatada;
                                const matchesTime = a.hora_inicio === time;
                                const matchesDoctor = filterDoctor.length === 0 || filterDoctor.includes(a.medico_nome || "");
                                const matchesStatus = filterStatus === "all" || a.status_agendamento.toUpperCase() === filterStatus.toUpperCase();
                                const matchesType = filterType === "all" ||
                                  (a.id_tipo_agenda && a.id_tipo_agenda.toString() === filterType) ||
                                  a.tipo_agenda_descricao?.toLowerCase().includes(filterType.toLowerCase());

                                return matchesDate && matchesTime && matchesDoctor && matchesStatus && matchesType;
                              }
                            );

                            return (
                              <div
                                key={`${index}-${time}`}
                                className="relative p-2 border-l border-muted-foreground/10 transition-all min-h-[60px]"
                              >
                                {agendamentosDoHorario.length > 0 ? (
                                  <div className="space-y-2">
                                    {agendamentosDoHorario.map((apt) => {
                                      const corBorda = getTipoAgendaCor(apt.id_tipo_agenda);
                                      return (
                                        <div
                                          key={apt.id}
                                          className="bg-primary/10 hover:bg-primary/20 cursor-pointer rounded-md p-2 transition-all"
                                          style={{
                                            borderLeftWidth: '4px',
                                            borderLeftColor: corBorda,
                                          }}
                                          onClick={() => {
                                            if (onAppointmentClick) {
                                              onAppointmentClick(apt);
                                            }
                                          }}
                                        >
                                          <div className="text-sm">
                                            <p className="font-medium">{apt.paciente_nome}</p>
                                            <p className="text-xs text-muted-foreground">{apt.medico_nome}</p>
                                            {!apt.meet_link &&
                                              !isGoogleMeetAutoEnabled &&
                                              apt.status_agendamento !== "CANCELADO" &&
                                              isMeetEnabledForType(apt.id_tipo_agenda) && (
                                                <Button
                                                  size="sm"
                                                  variant="outline"
                                                  className="mt-2 h-7 text-xs gap-1"
                                                  onClick={async (event) => {
                                                    event.stopPropagation();
                                                    await onCreateMeetLink(apt.id);
                                                  }}
                                                >
                                                  <Link className="h-3.5 w-3.5" />
                                                  Criar link Meet
                                                </Button>
                                              )}
                                            {apt.meet_link && apt.status_agendamento !== "CANCELADO" && (isAdmin || isMedico) && (
                                              <div className="flex gap-1 mt-2">
                                                <Button
                                                  size="sm"
                                                  className="h-7 text-xs gap-1"
                                                  onClick={(event) => {
                                                    event.stopPropagation();
                                                    handleStartAppointment(apt.meet_link);
                                                  }}
                                                >
                                                  <Video className="h-3.5 w-3.5" />
                                                  Iniciar atendimento
                                                </Button>
                                                <Button
                                                  size="sm"
                                                  variant="outline"
                                                  className="h-7 text-xs gap-1"
                                                  onClick={(event) => {
                                                    event.stopPropagation();
                                                    copyMeetLink(apt.meet_link);
                                                  }}
                                                  title="Copiar link da video conferência"
                                                >
                                                  <Copy className="h-3.5 w-3.5" />
                                                  Copiar link
                                                </Button>
                                              </div>
                                            )}
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                ) : (
                                  <p className="text-xs text-center text-muted-foreground italic">Livre</p>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      ));
                    })()}
                  </>
                );
              })()}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

